import { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, type MutableRefObject, type ReactNode, type CSSProperties } from 'react';
import { api } from '../lib/api.js';
import { Spinner } from '../components/Spinner.js';
import { Skeleton } from '../components/Skeleton.js';
import { useDelayedLoading } from '../hooks/useDelayedLoading.js';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  MeasuringStrategy,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Scatter,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import BlockTimelineChart from '../components/BlockTimelineChart.js';
import { ProjectFilterDropdown } from '../components/ProjectFilterDropdown.js';
import { SmokeStatusBadge } from '../components/SmokeStatusBadge.js';
import { SmokeVerdictPanel } from '../components/SmokeVerdictPanel.js';
import { AutoRegisteredBadge } from '../components/AutoRegisteredBadge.js';
import ServiceGroupHeader from '../components/ServiceGroupHeader.js';
import { LLMChip, LLMStatusLegend, type AgentLLMStatus } from '../components/pipeline/LLMChip.js';
import {
  SERVICE_GROUPS,
  groupForServiceKey,
  sortGroupEntries,
  type ServiceGroup,
} from '../lib/service-groups.js';
import type { SmokeSummary, SmokeDetail } from '../types/smoke.js';

// Inline SVG icons (matches project style — see Layout.jsx)
interface IconProps { d: string; className?: string; }
const Icon = ({ d, className = 'w-4 h-4' }: IconProps) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);
const RefreshCw    = (p) => <Icon {...p} d="M4 4v6h6M20 20v-6h-6M20 9A8 8 0 005.6 6.6L4 8m16 8a8 8 0 01-14.4 2.4L4 17" />;
const ChevronDown  = (p) => <Icon {...p} d="M19 9l-7 7-7-7" />;
const ChevronUp    = (p) => <Icon {...p} d="M5 15l7-7 7 7" />;
const ExternalLink = (p) => <Icon {...p} d="M14 5h5v5M19 5l-9 9M19 14v5H5V5h5" />;
const History = (p) => <Icon {...p} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />;
const GitHub = (p) => (
  <svg {...p} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

// ── Next tick pill ────────────────────────────────────────────────────────────
interface NextTickPillProps { agentTick?: { lastTickAt: string; intervalSec: number; nextTickAt: string } | null; }
export function NextTickPill({ agentTick }: NextTickPillProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  if (!agentTick) {
    return (
      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200">
        [--:--]
      </span>
    );
  }

  const remainingSec = Math.round((new Date(agentTick.nextTickAt).getTime() - now) / 1000);
  const displaySec = Math.max(remainingSec, 0);
  const mm = String(Math.floor(displaySec / 60)).padStart(2, '0');
  const ss = String(displaySec % 60).padStart(2, '0');
  const isOverdue = remainingSec <= 0;

  return (
    <span
      className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
        isOverdue
          ? 'bg-red-50 text-red-600 border-red-200'
          : 'bg-amber-50 text-amber-700 border-amber-200'
      }`}
      title={`Last tick: ${agentTick.lastTickAt} · Interval: ${agentTick.intervalSec}s`}
    >
      [{mm}:{ss}]
    </span>
  );
}

// ── Tick prediction card ─────────────────────────────────────────────────────
interface TickPredictionCardProps {
  prediction?: {
    total: number;
    byState: Record<string, number>;
    issues: Array<{ identifier: string; title: string; state: string }>;
  } | null;
}

export function TickPredictionCard({ prediction }: TickPredictionCardProps) {
  if (!prediction) return null;
  const { total, byState, issues } = prediction;
  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-indigo-700">Next tick prediction</span>
        <span className="text-xs text-indigo-500">{total} issue{total !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {Object.entries(byState).filter(([, count]) => (count as number) > 0).map(([state, count]) => (
          <span key={state} className="text-xs px-2 py-0.5 rounded-full bg-white text-indigo-600 border border-indigo-100">
            {STATE_EMOJI[state]} {state}: {count}
          </span>
        ))}
      </div>
      {issues.length > 0 && (
        <div className="space-y-1">
          {issues.map((issue, idx) => (
            <div key={issue.identifier} className="flex items-center gap-2 text-xs text-gray-600">
              {idx < 3 && (
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-medium flex-shrink-0">{idx + 1}</span>
              )}
              <span className="font-mono text-indigo-600">{issue.identifier}</span>
              <span className="truncate flex-1">{issue.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tick slider ──────────────────────────────────────────────────────────────
interface TickSliderProps {
  ticks: any[] | null;
  selectedTick: any;
  onSelectTick: (tick: any) => void;
}

const isFailedRun = (r: any) => r.outcome === 'failed' || r.outcome === 'error';

export function TickSlider({ ticks, selectedTick, onSelectTick }: TickSliderProps) {
  if (ticks === null) {
    return <TickSliderSkeleton />;
  }
  if (ticks.length === 0) {
    return <div className="text-gray-400 text-xs">No tick history yet</div>;
  }

  const maxRuns = Math.max(1, ...ticks.map(t => t.actual?.processedCount || 0));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {ticks.map((tick) => {
          const isSelected = selectedTick?.id === tick.id;
          const runs = tick.actual?.processedCount || 0;
          const heightPct = Math.max(15, (runs / maxRuns) * 100);
          return (
            <button
              key={tick.id}
              onClick={() => onSelectTick(isSelected ? null : tick)}
              className={`flex flex-col items-center gap-1 min-w-[48px] ${isSelected ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
            >
              <div className="w-8 bg-gray-200 rounded-t relative" style={{ height: '48px' }}>
                <div
                  className={`absolute bottom-0 left-0 right-0 rounded-t ${tick.status === 'ok' ? 'bg-green-400' : 'bg-yellow-400'}`}
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-500 whitespace-nowrap">
                {new Date(tick.tickAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
          );
        })}
      </div>

      {selectedTick && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-gray-700">
              Tick at {new Date(selectedTick.tickAt).toLocaleString()}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full text-white font-medium ${selectedTick.status === 'ok' ? 'bg-green-500' : 'bg-yellow-500'}`}>
              {selectedTick.status}
            </span>
          </div>
          {selectedTick.status !== 'ok' && (
            (() => {
              const runs = selectedTick.actual?.runs || [];
              const failedRuns = runs.filter(isFailedRun);
              const failedCount = failedRuns.length;
              if (failedCount === 0) {
                return (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded px-2 py-1 text-xs mb-2">
                    ⚠️ Tick reported "{selectedTick.status}" — no failed run recorded.
                  </div>
                );
              }
              const first = failedRuns[0];
              const extra = failedCount - 1;
              return (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded px-2 py-1 text-xs mb-2">
                  ⚠️ {failedCount} run{failedCount !== 1 ? 's' : ''} failed:{' '}
                  {first.ticket_id || '—'} · {first.agent} · {first.outcome || 'pending'}
                  {first.duration_ms != null && ` · ${Math.round(first.duration_ms / 1000)}s`}
                  {extra > 0 && ` + ${extra} more`}
                </div>
              );
            })()
          )}
          <div className="text-xs text-gray-500 mb-2">
            Duration: {selectedTick.durationMs ? `${Math.round(selectedTick.durationMs / 1000)}s` : '—'}
            {' · '}
            Processed: {selectedTick.actual?.processedCount || 0}
            {selectedTick.backlogSize != null && ` · Backlog: ${selectedTick.backlogSize}`}
          </div>
          {selectedTick.actual?.runs?.length > 0 && (
            <ul className="space-y-1">
              {selectedTick.actual.runs.map((run: any, i: number) => {
                const isFailed = run.outcome === 'failed' || run.outcome === 'error';
                return (
                  <li key={i} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-indigo-600">{run.ticket_id || '—'}</span>
                      <span className="text-gray-600 capitalize">{run.agent}</span>
                      <span className={`px-1 py-0.5 rounded text-[10px] ${
                        run.outcome === 'success' || run.outcome === 'approved' || run.outcome === 'spec_written'
                          ? 'bg-green-100 text-green-700'
                          : isFailed
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {run.outcome || 'pending'}
                      </span>
                      {run.duration_ms != null && (
                        <span className="text-gray-400">{Math.round(run.duration_ms / 1000)}s</span>
                      )}
                    </div>
                    {isFailed && (
                      <div className={`mt-0.5 ${run.error_message ? 'text-red-600' : 'text-gray-400'}`}>
                        {run.error_message || 'No details'}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Status pill ───────────────────────────────────────────────────────────────
interface StatusPillProps { relay?: { running?: boolean } | null; queue?: { failedTotal?: number } | null; }
export function StatusPill({ relay, queue }: StatusPillProps) {
  if (!relay?.running) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-semibold bg-red-500 shrink-0 whitespace-nowrap">
        <span className="w-2 h-2 rounded-full bg-white/80" />
        Error: relay down
      </span>
    );
  }
  if (queue?.failedTotal > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-semibold bg-yellow-500 shrink-0 whitespace-nowrap">
        <span className="w-2 h-2 rounded-full bg-white/80" />
        {queue.failedTotal} failed in queue
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-semibold bg-green-500 shrink-0 whitespace-nowrap">
      <span className="w-2 h-2 rounded-full bg-white/80" />
      Running
    </span>
  );
}

// ── Shared colors ────────────────────────────────────────────────────────────

const IMPLEMENTER_COLORS = {
  kimi: '#10B981',     // emerald-500
  hack_bot: '#F59E0B', // amber-500
};

// ── Linear board ──────────────────────────────────────────────────────────────
// NOTE: keep in sync with the matching constant in server/routes/pipeline.ts
const STATE_EMOJI = {
  'Spec: Todo': '🔔',
  'Spec: Ready': '✅',
  'Doing': '🛠',
  'PR: Review Requested': '🔍',
  'PR: Changes Requested': '🔄',
  'PR: Review Passed': '🟢',
  'Blocked': '🚧',
};

const LINEAR_STATES = [
  'Spec: Todo',
  'Spec: Ready',
  'Doing',
  'PR: Review Requested',
  'PR: Changes Requested',
  'PR: Review Passed',
  'Blocked',
];

const SMOKE_BADGE_STATES = new Set([
  'PR: Review Requested',
  'PR: Changes Requested',
  'PR: Review Passed',
  'Blocked',
]);

// Owner badge configuration
const OWNER_BY_STATE = {
  'Spec: Todo':            { name: 'Claude Code Agent', color: '#1a237e' },  // midnight blue
  'PR: Review Requested':  { name: 'Claude Code Agent', color: '#1a237e' },
  'Spec: Ready':           { name: 'Kimi Code Agent',   color: '#FFC107' },  // gold
  'Doing':                 { name: 'Kimi Code Agent',   color: '#FFC107' },
  'PR: Changes Requested': { name: 'Kimi Code Agent',   color: '#FFC107' },
  'PR: Review Passed':     { name: 'Human',             color: '#E53935' },  // red
  // 'Blocked' and anything else → no badge
};

// Unique owners for the legend
const UNIQUE_OWNERS = [
  { name: 'Claude Code Agent', color: '#1a237e' },
  { name: 'Kimi Code Agent',   color: '#FFC107' },
  { name: 'Human',             color: '#E53935' },
];

// CSS for animations (injected inline since we don't have a separate CSS file)
const AnimationStyles = () => (
  <style>{`
    @keyframes card-pulse {
      0%, 100% { background-color: rgb(255, 255, 255); }
      50% { background-color: rgb(254, 249, 231); }
    }
    @keyframes card-leaving-pulse {
      0%, 100% { background-color: rgb(255, 255, 255); }
      50% { background-color: rgb(254, 226, 226); }
    }
    @keyframes card-entering-pulse {
      0%, 100% { background-color: rgb(255, 255, 255); }
      50% { background-color: rgb(220, 252, 231); }
    }
    @keyframes card-destruct {
      from { opacity: 1; transform: scale(1); }
      to { opacity: 0; transform: scale(0.85); }
    }
    @keyframes card-spawn {
      from { opacity: 0; transform: scale(0.85); }
      to { opacity: 1; transform: scale(1); }
    }
    .card-leaving {
      animation: card-leaving-pulse 2s ease-in-out, card-destruct 0.4s ease-out 2s forwards;
    }
    .card-entering {
      animation: card-spawn 0.4s ease-out, card-entering-pulse 2s ease-in-out 0.4s;
    }
    .column-transition {
      transition: width 300ms ease-out, min-width 300ms ease-out, max-width 300ms ease-out;
    }
    @keyframes card-active-pulse {
      0%, 100% { box-shadow: 0 0 0 0 transparent; }
      50% { box-shadow: 0 0 0 2px var(--pulse-color); }
    }
    .card-active {
      border-radius: 0.375rem;
      animation: card-active-pulse 2s ease-in-out infinite;
    }
  `}</style>
);

function OwnerLegend() {
  return (
    <div className="flex items-center gap-4 text-xs text-gray-500">
      <span className="text-gray-400">Owners:</span>
      {UNIQUE_OWNERS.map((owner) => (
        <div key={owner.name} className="flex items-center gap-1.5" title={owner.name}>
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: owner.color }}
          />
          <span className="max-w-[120px] truncate">{owner.name}</span>
        </div>
      ))}
    </div>
  );
}

// ── Skeleton placeholders ─────────────────────────────────────────────────────

export function LinearBoardSkeleton() {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {LINEAR_STATES.map((state) => (
        <div
          key={state}
          className="column-transition bg-gray-50 rounded-lg border border-gray-200 flex flex-col p-3"
          style={{ minWidth: '280px', flex: '1 1 0', minHeight: '320px' }}
        >
          <div className="flex items-center gap-1 mb-3">
            <Skeleton className="w-4 h-4 rounded-full" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-8 ml-auto" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-md p-2 space-y-2">
                <div className="flex items-start gap-1">
                  <Skeleton className="h-3 w-14 shrink-0" />
                  <Skeleton className="h-3 w-16 shrink-0" />
                  <Skeleton className="h-3 w-full" />
                </div>
                <div className="flex items-center justify-between pl-2">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ServiceHealthSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {['24h', '7d', '30d'].map((r) => (
          <Skeleton key={r} className="h-7 w-20" />
        ))}
      </div>
      <div className="space-y-3">
        {SERVICE_GROUPS.map((group) => (
          <div key={group} className="border border-gray-200 rounded-lg p-3 bg-white space-y-3">
            <Skeleton className="h-5 w-32" />
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <div className="flex gap-0.5 h-6">
                  {Array.from({ length: 3 }).map((idx) => (
                    <Skeleton key={idx} className="h-full flex-1 rounded-sm" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function PipelineHealthSkeleton() {
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="h-3 w-16" />
      </div>
      <div className="flex items-end gap-2 h-12">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton
            key={i}
            className="w-8 rounded-t"
            style={{ height: `${Math.max(20, 60 + Math.sin(i) * 30)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

export function CompletionChartSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {['24h', '3d', '1W', '2W', '4W', '2M', '3M'].map((o) => (
          <Skeleton key={o} className="h-6 w-10" />
        ))}
      </div>
      <div className="h-64 flex items-end gap-1 px-2 pb-6 border-b border-l border-gray-200">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1 rounded-t"
            style={{ height: `${30 + ((i % 5) + 1) * 12}%`, opacity: 0.6 + (i % 3) * 0.15 }}
          />
        ))}
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  );
}

export function AgentStatsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {['Kimi', 'Claude'].map((agent) => (
            <div key={agent} className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-5 w-10 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
        <div className="flex gap-1">
          {['24h', '7d', '30d', '2m', '3m'].map((p) => (
            <Skeleton key={p} className="h-6 w-10" />
          ))}
        </div>
      </div>
      <Skeleton className="h-44 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function FailingTicketsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-24 ml-auto" />
          </div>
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-full" />
          <div className="flex gap-2 pt-1">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TickSliderSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-1 min-w-[48px]">
            <Skeleton className="w-8 rounded-t" style={{ height: `${30 + (i % 5) * 12}px` }} />
            <Skeleton className="h-3 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── History Timeline ─────────────────────────────────────────────────────────
// Actor colors for history dots (same as OWNER_BY_STATE for consistency)
const ACTOR_COLORS = {
  claude: '#1a237e',   // midnight blue
  openclaw: '#FFC107', // gold
  human: '#E53935',    // red
  system: '#9E9E9E',   // grey for system/null actors
};

/**
 * HistoryTimeline — inline state transition history for an issue.
 * Shows last 5 transitions, with a "+ N more" expander if there are more.
 */
interface HistoryTimelineProps { issueIdentifier: string; historyCache: MutableRefObject<Record<string, any[]>>; }
function HistoryTimeline({ issueIdentifier, historyCache }: HistoryTimelineProps) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchHistory() {
      // Check cache first
      if (historyCache.current[issueIdentifier]) {
        setHistory(historyCache.current[issueIdentifier]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`/api/pipeline/issue/${issueIdentifier}/history`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        if (!cancelled) {
          historyCache.current[issueIdentifier] = data.history || [];
          setHistory(data.history || []);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchHistory();
    return () => { cancelled = true; };
  }, [issueIdentifier, historyCache]);

  if (loading) {
    return (
      <div className="py-2">
        <Spinner size={16} className="justify-start" />
      </div>
    );
  }

  if (error) {
    return <div className="text-xs text-gray-400 py-2">Could not load history</div>;
  }

  if (!history || history.length === 0) {
    return <div className="text-xs text-gray-400 py-2">No history yet</div>;
  }

  // Show last 5 by default, or all if expanded
  const displayCount = expanded ? history.length : Math.min(5, history.length);
  const displayItems = history.slice(0, displayCount);
  const remaining = history.length - displayCount;

  return (
    <div className="py-2 border-t border-gray-100 mt-2">
      <ul className="space-y-1.5">
        {displayItems.map((item) => (
          <li key={item.id} className="flex items-center gap-2 text-xs">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: ACTOR_COLORS[item.actor?.kind] || ACTOR_COLORS.system }}
              title={item.actor?.kind}
            />
            <span className="font-mono text-gray-600">
              {item.fromState || '—'} → {item.toState || '—'}
            </span>
            <span className="text-gray-400">by {item.actor?.displayName || 'Unknown'}</span>
            <span
              className="text-gray-400 ml-auto shrink-0"
              title={new Date(item.createdAt).toLocaleString()}
            >
              {relativeTime(item.createdAt)}
            </span>
          </li>
        ))}
      </ul>
      {remaining > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs text-indigo-600 hover:text-indigo-700 mt-1.5"
        >
          + {remaining} more
        </button>
      )}
    </div>
  );
}

interface OwnerBadgeProps { state: string; }
export function OwnerBadge({ state }: OwnerBadgeProps) {
  const owner = OWNER_BY_STATE[state];
  if (!owner) return null;

  return (
    <div
      className="absolute left-0 top-0 bottom-0 w-1 rounded-l"
      style={{ backgroundColor: owner.color }}
      title={`Owner: ${owner.name}`}
    />
  );
}

interface AssigneeBadgeProps { assignee?: { displayName?: string; name?: string } | null; implementer?: string; }
export function AssigneeBadge({ assignee, implementer }: AssigneeBadgeProps) {
  if (!assignee) return null;
  const color = IMPLEMENTER_COLORS[implementer] || '#9CA3AF';
  const name = assignee.displayName || assignee.name || 'Unknown';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600 border border-gray-200"
      title={name}
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="truncate max-w-[80px]">{name}</span>
    </span>
  );
}

// ── DnD helpers ───────────────────────────────────────────────────────────────

function findIssueInLinear(linear: any, identifier: string): any | null {
  if (!linear?.byState) return null;
  for (const state of LINEAR_STATES) {
    const found = linear.byState[state]?.find((i: any) => i.identifier === identifier);
    if (found) return found;
  }
  return null;
}

function DroppableColumn({ state, children, style, className }: {
  state: string;
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: state });
  return (
    <div
      ref={setNodeRef}
      className={`${className || ''} ${isOver ? 'ring-2 ring-inset ring-indigo-300 bg-indigo-50/30' : ''}`}
      style={style}
    >
      {children}
    </div>
  );
}

function DraggableCard({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50, position: 'relative' }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={isDragging ? 'opacity-50' : ''}
    >
      {children}
    </div>
  );
}

function DragOverlayCard({ issue }: { issue: any }) {
  if (!issue) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-lg px-2 py-1.5 w-full max-w-[280px]">
      <div className="flex items-start gap-1">
        <span className="text-xs font-mono text-indigo-600 shrink-0 mt-0.5">{issue.identifier}</span>
        <span className="text-xs text-gray-700 line-clamp-2 leading-tight flex-1">{issue.title}</span>
      </div>
    </div>
  );
}

interface LinearBoardProps {
  linear?: { byState?: Record<string, any[]>; totals?: Record<string, number> } | null;
  leavingCards?: Record<string, { issue: any; oldState: string; timestamp: number }>;
  cardStates?: Record<string, string>;
  expandedCardId?: string | null;
  onToggleCard: (id: string) => void;
  historyCache: MutableRefObject<Record<string, any[]>>;
  activeAgents?: { claude?: { task?: string } | null; kimi?: { task?: string } | null } | null;
  loading?: boolean;
  isDragging?: boolean;
  filterEmpty?: boolean;
  onResetFilter?: () => void;
  smokeSummary?: Record<string, SmokeSummary> | null;
  smokeDetailCache?: MutableRefObject<Record<string, SmokeDetail>>;
}
export function BoardCard({ issue, state, cardStates, expandedCardId, onToggleCard, historyCache, activeTasks, owner, smokeSummary, smokeDetailCache }: {
  issue: any;
  state: string;
  cardStates: Record<string, string>;
  expandedCardId: string | null;
  onToggleCard: (id: string) => void;
  historyCache: MutableRefObject<Record<string, any[]>>;
  activeTasks: Set<string>;
  owner: any;
  smokeSummary?: Record<string, SmokeSummary> | null;
  smokeDetailCache?: MutableRefObject<Record<string, SmokeDetail>>;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: issue.identifier,
  });
  const style: CSSProperties = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50, position: 'relative' }
    : undefined;

  const cardKey = issue.identifier;
  const cardState = cardStates[cardKey];
  const isEntering = cardState === 'entering';
  const isExpanded = expandedCardId === cardKey;
  const isCardActive = owner && owner.name !== 'Human' && activeTasks.has(issue.identifier);
  const smokeEntry = smokeSummary?.[cardKey];
  const showSmokeBadge = SMOKE_BADGE_STATES.has(state);

  const [smokeDetail, setSmokeDetail] = useState<SmokeDetail | null>(null);
  const [smokeDetailLoading, setSmokeDetailLoading] = useState(() => isExpanded);
  const [smokeDetailError, setSmokeDetailError] = useState<string | null>(null);

  // Show the loading state synchronously when expanding so the panel does not
  // flash "No smoke verdict recorded." before the fetch starts.
  useLayoutEffect(() => {
    if (!isExpanded || !smokeDetailCache) return;
    if (smokeDetailCache.current[cardKey]) return;
    setSmokeDetailLoading(true);
  }, [isExpanded, cardKey, smokeDetailCache]);

  useEffect(() => {
    if (!isExpanded || !smokeDetailCache) return;
    let cancelled = false;

    async function fetchSmokeDetail() {
      const cached = smokeDetailCache.current[cardKey];
      if (cached) {
        setSmokeDetail(cached);
        setSmokeDetailLoading(false);
        return;
      }

      setSmokeDetailLoading(true);
      setSmokeDetailError(null);
      try {
        const res = await fetch(`/api/pipeline/smoke/${encodeURIComponent(cardKey)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          smokeDetailCache.current[cardKey] = json;
          setSmokeDetail(json);
        }
      } catch (err: any) {
        if (!cancelled) setSmokeDetailError(err.message || 'Failed to load smoke detail');
      } finally {
        if (!cancelled) setSmokeDetailLoading(false);
      }
    }

    fetchSmokeDetail();
    return () => { cancelled = true; };
  }, [isExpanded, cardKey, smokeDetailCache]);

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`relative ${isEntering ? 'card-entering' : ''} ${isDragging ? 'opacity-50' : ''}`}
    >
      <button
        onClick={() => onToggleCard(cardKey)}
        className={`w-full text-left block rounded-md relative ${isCardActive ? 'card-active' : ''}`}
        style={isCardActive ? { ['--pulse-color' as any]: owner.color } : undefined}
      >
        <OwnerBadge state={state} />
        <div className="flex items-start gap-1 group hover:bg-white rounded px-1 -mx-1 transition-colors bg-white/50 py-1 pl-2">
          <span className="text-xs font-mono text-indigo-600 shrink-0 mt-0.5 ml-1">{issue.identifier}</span>
          {issue.project && (
            <span style={{
              fontSize: '0.65rem',
              padding: '1px 6px',
              borderRadius: '9999px',
              backgroundColor: '#e0e7ff',
              color: '#4338ca',
              marginLeft: '6px',
              fontWeight: 500
            }}>
              {issue.project}
            </span>
          )}
          <span className="text-xs text-gray-700 line-clamp-2 leading-tight flex-1">{issue.title}</span>
          {issue.prUrl && (
            <a
              href={issue.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 mt-0.5"
              title="Open PR on GitHub"
            >
              <GitHub className="w-2.5 h-2.5 text-gray-300 hover:text-gray-500" />
            </a>
          )}
          <a
            href={issue.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 mt-0.5"
            title="Open in Linear"
          >
            <ExternalLink className="w-2.5 h-2.5 text-gray-300 hover:text-gray-500" />
          </a>
        </div>
        <div className="flex items-center justify-between pl-3 pr-1">
          <span className="text-gray-400 text-xs">{relativeTime(issue.updatedAt)}</span>
          <div className="flex items-center gap-1.5">
            {showSmokeBadge && (
              <SmokeStatusBadge
                status={smokeEntry?.status ?? 'not_run'}
                summary={
                  smokeEntry
                    ? {
                        routeCount: smokeEntry.routeCount,
                        failureCount: smokeEntry.failureCount,
                        elapsedMs: smokeEntry.elapsedMs,
                      }
                    : undefined
                }
              />
            )}
            <AssigneeBadge assignee={issue.assignee} implementer={issue.implementer} />
          </div>
        </div>
      </button>
      {isExpanded && (
        <HistoryTimeline
          issueIdentifier={cardKey}
          historyCache={historyCache}
        />
      )}
      {isExpanded && showSmokeBadge && (
        <div className="border-t border-gray-100 mt-2 pt-2">
          <SmokeVerdictPanel
            detail={smokeDetail}
            loading={smokeDetailLoading}
            error={smokeDetailError}
          />
        </div>
      )}
    </li>
  );
}

function LinearBoard({ linear, leavingCards, cardStates, expandedCardId, onToggleCard, historyCache, activeAgents, loading, isDragging = false, filterEmpty, onResetFilter, smokeSummary, smokeDetailCache }: LinearBoardProps) {
  if (loading) return <LinearBoardSkeleton />;
  if (!linear) return <div className="text-gray-400 text-sm italic">Linear data unavailable</div>;
  if (filterEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 border border-gray-200 rounded-lg bg-gray-50">
        <p className="text-sm text-gray-500 mb-3">No tickets match the current project filter</p>
        <button
          onClick={onResetFilter}
          className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          Reset filter
        </button>
      </div>
    );
  }

  const activeTasks = new Set(
    [activeAgents?.claude?.task, activeAgents?.kimi?.task].filter(Boolean)
  );

  // Track which columns have issues for stable layout
  const columnsWithIssues = LINEAR_STATES.filter(state => (linear.byState?.[state] || []).length > 0);
  const allColumnsEmpty = columnsWithIssues.length === 0;

  // Determine column width: if all empty, full width; otherwise fixed minmax for non-empty, 40px for empty
  const getColumnStyle = (state) => {
    if (allColumnsEmpty) {
      return { minWidth: '160px', flex: '1 1 0' };
    }
    const hasIssues = (linear.byState?.[state] || []).length > 0;
    if (hasIssues) {
      return { minWidth: '280px', flex: '1 1 0' };
    }
    if (isDragging) {
      return { minWidth: '160px', flex: '1 1 0' };
    }
    return { width: '40px', minWidth: '40px', flex: '0 0 auto' };
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {LINEAR_STATES.map((state) => {
        const issues = linear.byState?.[state] || [];
        const isEmpty = issues.length === 0;
        const columnStyle = getColumnStyle(state);
        const isCollapsed = !allColumnsEmpty && isEmpty && !isDragging;
        const owner = OWNER_BY_STATE[state];
        
        // Get leaving cards for this column
        const leavingHere = Object.values(leavingCards || {}).filter(p => p.oldState === state);

        return (
          <DroppableColumn
            key={state}
            state={state}
            className={`bg-gray-50 rounded-lg border border-gray-200 flex flex-col ${isDragging ? '' : 'column-transition'}`}
            style={{ ...columnStyle, minHeight: '80px' }}
          >
            {/* Column header */}
            <div 
              className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1 px-3 pt-3"
              style={isCollapsed ? { writingMode: 'vertical-rl', transform: 'rotate(180deg)' } : {}}
            >
              <span>{STATE_EMOJI[state]}</span>
              {!isCollapsed && <span className="truncate">{state}</span>}
              <span className={`${isCollapsed ? 'mt-1' : 'ml-auto'} text-gray-400`}>
                ({issues.length})
              </span>
            </div>

            {/* Issues list */}
            {!isCollapsed && (
              <div className="px-2 pb-3 flex-1">
                {issues.length === 0 && leavingHere.length === 0 ? (
                  <div className="text-gray-300 text-xs px-1">—</div>
                ) : (
                  <ul className="space-y-1.5">
                    {/* Render real issues */}
                    {issues.map((issue) => (
                      <BoardCard
                        key={issue.identifier}
                        issue={issue}
                        state={state}
                        cardStates={cardStates || {}}
                        expandedCardId={expandedCardId || null}
                        onToggleCard={onToggleCard}
                        historyCache={historyCache}
                        activeTasks={activeTasks}
                        owner={owner}
                        smokeSummary={smokeSummary}
                        smokeDetailCache={smokeDetailCache}
                      />
                    ))}
                    {/* Render leaving (phantom) cards */}
                    {leavingHere.map((phantom) => {
                      const issue = phantom.issue;
                      const phantomKey = `${issue.identifier}-leaving-${phantom.timestamp}`;
                      const isCardActive = owner && owner.name !== 'Human' && activeTasks.has(issue.identifier);
                      
                      return (
                        <li
                          key={phantomKey}
                          className="relative card-leaving"
                        >
                          <div className={`rounded-md relative ${isCardActive ? 'card-active' : ''}`} style={isCardActive ? { ['--pulse-color' as any]: owner.color } : undefined}>
                            <OwnerBadge state={state} />
                            <div className="flex items-start gap-1 group rounded px-1 -mx-1 bg-white/50 py-1 pl-2 opacity-70">
                              <span className="text-xs font-mono text-indigo-600 shrink-0 mt-0.5 ml-1">{issue.identifier}</span>
                              <span className="text-xs text-gray-700 line-clamp-2 leading-tight">{issue.title}</span>
                              {issue.prUrl && <GitHub className="w-2.5 h-2.5 shrink-0 mt-0.5 text-gray-300" />}
                              <ExternalLink className="w-2.5 h-2.5 shrink-0 mt-0.5 text-gray-300" />
                            </div>
                            <div className="flex items-center justify-between pl-3 pr-1">
                              <span className="text-gray-400 text-xs">{relativeTime(issue.updatedAt)}</span>
                              <AssigneeBadge assignee={issue.assignee} implementer={issue.implementer} />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </DroppableColumn>
        );
      })}
    </div>
  );
}

// ── Recent activity ───────────────────────────────────────────────────────────
const EVENT_EMOJI = {
  spec_needed: '🔔',
  spec_written: '✅',
  pr_opened: '🔍',
  pr_reviewed: '🎉',
  review_passed: '🎉',
  changes_requested: '🔄',
  error: '❌',
};

interface RecentActivityProps { queue?: any; relay?: any; loading?: boolean; }
function RecentActivity({ queue, relay, loading }: RecentActivityProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <Skeleton className="h-4 w-32 mb-2" />
          <div className="space-y-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-3 w-4" />
                <Skeleton className="h-3 w-10" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        </div>
        <div>
          <Skeleton className="h-4 w-28 mb-2" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {/* Processed files */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Queue activity (last 20)</h3>
        {(!queue?.processedRecent?.length) ? (
          <div className="text-gray-400 text-sm">No processed files yet</div>
        ) : (
          <ul className="space-y-1">
            {queue.processedRecent.map((f) => (
              <li key={f.name} className="flex items-center gap-2 text-sm text-gray-700">
                <span className="shrink-0">{EVENT_EMOJI[f.event] || 'ℹ️'}</span>
                <span className="text-gray-400 text-xs tabular-nums shrink-0">
                  {f.mtime ? new Date(f.mtime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                </span>
                <span className="font-mono text-xs text-indigo-600 shrink-0">{f.issue_id || ''}</span>
                <span className="truncate text-gray-600">{f.summary || f.event || f.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Log tail */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Relay log (last 20 lines)</h3>
        <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto max-h-48 overflow-y-auto font-mono leading-relaxed">
          {relay?.logTail?.length
            ? relay.logTail.join('\n')
            : <span className="text-gray-500">No log output</span>}
        </pre>
      </div>
    </div>
  );
}

// ── Failed panel ──────────────────────────────────────────────────────────────
interface FailedPanelProps { queue?: any; }
function FailedPanel({ queue }: FailedPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const count = queue?.failedTotal ?? 0;

  return (
    <div className="border border-red-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors text-sm font-medium text-red-700"
      >
        <span>Failed queue ({count} total)</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="p-4 bg-white">
          {!queue?.failedRecent?.length ? (
            <div className="text-gray-400 text-sm">No failed files</div>
          ) : (
            <ul className="space-y-2">
              {queue.failedRecent.map((f) => (
                <li key={f.name} className="text-sm border-b border-gray-100 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-red-600">{f.name}</span>
                    <span className="text-gray-400 text-xs">{relativeTime(f.mtime)}</span>
                  </div>
                  {f.event && <div className="text-gray-500 text-xs">event: {f.event}</div>}
                  {f.parseError && <div className="text-orange-500 text-xs">JSON parse error</div>}
                  {/* v1: Retry button is a placeholder — endpoint not yet implemented */}
                  <button
                    disabled
                    title="Retry (not yet implemented)"
                    className="mt-1 text-xs text-gray-300 border border-gray-200 px-2 py-0.5 rounded cursor-not-allowed"
                  >
                    Retry (v2)
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Health / Uptime ───────────────────────────────────────────────────────────

const STATUS_COLORS = {
  ok: 'bg-green-500',
  idle: 'bg-gray-300',
  warning: 'bg-yellow-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-400',
  error: 'bg-red-500',
  unscheduled: 'bg-slate-300',
};

const STATUS_LABELS = {
  ok: 'OK',
  idle: 'Idle',
  warning: 'Warning',
  down: 'Down',
  unknown: 'Unknown',
  error: 'Error',
  unscheduled: 'Unscheduled',
};

function ServiceHealthBar({ serviceId, svc, current, selectedBucket, setSelectedBucket }: {
  serviceId: string;
  svc: any;
  current: any;
  selectedBucket: any;
  setSelectedBucket: (b: any) => void;
}) {
  const buckets = svc.buckets || [];
  const displayLabel = svc.display_name || serviceId;
  const fullLabel = svc.display_name ? `${displayLabel} · ${serviceId}` : displayLabel;
  return (
    <div key={serviceId} className="space-y-1">
      <div className="flex items-center justify-between">
        <span
          className="text-xs font-medium text-gray-700 inline-flex items-center gap-1.5"
          title={fullLabel}
        >
          {displayLabel}
          {svc.display_name && (
            <span className="text-gray-400 font-normal">· {serviceId}</span>
          )}
          {svc.auto_registered && <AutoRegisteredBadge />}
        </span>
        <span className="text-xs text-gray-400">
          {svc.scheduled === false
            ? 'not scheduled'
            : current
              ? `${current.age_minutes ?? '—'}m ago · every ${svc.expected_interval_min}m`
              : `every ${svc.expected_interval_min}m`}
        </span>
      </div>
      <div className="flex gap-0.5 h-6">
        {buckets.map((b: any, idx: number) => {
          const isSelected = selectedBucket?.serviceId === serviceId && selectedBucket?.idx === idx;
          return (
            <button
              key={idx}
              onClick={() => setSelectedBucket({ serviceId, idx, ...b })}
              title={`${new Date(b.time).toLocaleString()} — ${b.status}${b.backlog_size != null ? `, backlog: ${b.backlog_size}` : ''}${b.processed_this_run != null ? `, processed this run: ${b.processed_this_run}` : ''}`}
              className={`h-full rounded-sm ${STATUS_COLORS[b.status] || 'bg-gray-400'} ${isSelected ? 'ring-2 ring-indigo-500' : 'hover:opacity-80'}`}
              style={{ width: `${100 / buckets.length}%` }}
            />
          );
        })}
      </div>
    </div>
  );
}

interface UptimeChartProps {
  healthData?: any;
  historyData?: any;
  range: string;
  setRange: (r: string) => void;
  selectedBucket: any;
  setSelectedBucket: (b: any) => void;
}
function UptimeChart({ healthData, historyData, range, setRange, selectedBucket, setSelectedBucket }: UptimeChartProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('service_health_collapsed') || '{}'); }
    catch { return {}; }
  });
  const toggleGroup = (g: string) => setCollapsedGroups(prev => {
    const next = { ...prev, [g]: !prev[g] };
    localStorage.setItem('service_health_collapsed', JSON.stringify(next));
    return next;
  });
  const loading = useDelayedLoading(!healthData || !historyData);
  if (loading || !healthData || !historyData) {
    return <ServiceHealthSkeleton />;
  }

  const services = Object.entries(historyData.services || {});
  if (services.length === 0) {
    return <div className="text-gray-400 text-sm">No services configured</div>;
  }

  return (
    <div className="space-y-4">
      {/* Range selector */}
      <div className="flex items-center gap-2">
        {['24h', '7d', '30d'].map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
              range === r
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {r === '24h' ? 'Last 24h' : r === '7d' ? 'Last 7 days' : 'Last 30 days'}
          </button>
        ))}
      </div>

      {/* Uptime bars */}
      <div className="space-y-3">
        {SERVICE_GROUPS.map((group) => {
          const entries = sortGroupEntries(
            group,
            Object.entries(historyData.services || {}).filter(
              ([id]) => groupForServiceKey(id) === group
            )
          );
          if (entries.length === 0) return null;
          const isCollapsed = !!collapsedGroups[group];
          return (
            <div key={group} className="border border-gray-200 rounded-lg p-3 bg-white">
              <ServiceGroupHeader
                group={group}
                collapsed={isCollapsed}
                onToggle={() => toggleGroup(group)}
              />
              {!isCollapsed && (
                <div className="mt-2 space-y-3">
                  {entries.map(([serviceId, svc]) => {
                    const current = healthData.services?.[serviceId];
                    return (
                      <ServiceHealthBar key={serviceId} serviceId={serviceId} svc={svc} current={current} selectedBucket={selectedBucket} setSelectedBucket={setSelectedBucket} />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected bucket detail */}
      {selectedBucket && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-gray-800">
              {historyData.services[selectedBucket.serviceId]?.display_name || selectedBucket.serviceId}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full text-white font-medium ${STATUS_COLORS[selectedBucket.status] || 'bg-gray-400'}`}>
              {STATUS_LABELS[selectedBucket.status] || selectedBucket.status}
            </span>
          </div>
          <div className="text-xs text-gray-500 mb-2">
            {new Date(selectedBucket.time).toLocaleString()}
          </div>
          {(selectedBucket.backlog_size != null || selectedBucket.processed_this_run != null) && (
            <div className="text-xs text-gray-600 mb-2">
              {selectedBucket.backlog_size != null && <div>backlog: {selectedBucket.backlog_size}</div>}
              {selectedBucket.processed_this_run != null && <div>processed this run: {selectedBucket.processed_this_run}</div>}
            </div>
          )}
          {selectedBucket.metadata && Object.keys(selectedBucket.metadata).length > 0 && (
            <pre className="bg-white border border-gray-200 rounded p-2 text-xs text-gray-700 overflow-x-auto">
              {JSON.stringify(selectedBucket.metadata, null, 2)}
            </pre>
          )}
          <button
            onClick={() => setSelectedBucket(null)}
            className="mt-2 text-xs text-indigo-600 hover:text-indigo-700"
          >
            Close details
          </button>
        </div>
      )}
    </div>
  );
}

// ── Pipeline Health Tile ────────────────────────────────────────────────────

interface PipelineHealthTileProps { data?: any; }
export function PipelineHealthTile({ data }: PipelineHealthTileProps) {
  const [expanded, setExpanded] = useState(false);
  const loading = useDelayedLoading(!data);

  if (loading || !data) {
    return <PipelineHealthSkeleton />;
  }

  const { status, stale, age_minutes, timestamp, last_run, findings, auto_fixes_applied, reason } = data;

  // Determine display color
  let colorClass = 'bg-gray-400';
  let label = 'Unknown';

  const isTooOld = typeof age_minutes === 'number' && age_minutes > 180; // 3h

  if (status === 'ok' && !stale) {
    colorClass = 'bg-green-500';
    label = 'Healthy';
  } else if (status === 'issues' || stale) {
    colorClass = 'bg-yellow-500';
    label = stale ? 'Stale' : 'Issues';
  } else if (status === 'unknown' && isTooOld) {
    colorClass = 'bg-red-500';
    label = 'Dead';
  } else if (status === 'unknown') {
    colorClass = 'bg-yellow-500';
    label = 'Unknown';
  }

  const timeLabel = typeof age_minutes === 'number'
    ? (age_minutes < 0 ? 'just now' : `${age_minutes}m ago`)
    : (timestamp || last_run ? relativeTime(timestamp || last_run) : '—');

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-semibold ${colorClass}`}>
            <span className="w-2 h-2 rounded-full bg-white/80" />
            {label}
          </span>
          <span className="text-sm font-medium text-gray-700">Pipeline Health (hourly)</span>
        </div>
        <span className="text-xs text-gray-400">{timeLabel}</span>
      </div>

      {reason && (
        <div className="mt-2 text-xs text-gray-500">{reason}</div>
      )}

      {Array.isArray(auto_fixes_applied) && auto_fixes_applied.length > 0 && (
        <div className="mt-2 text-xs text-green-600">
          Auto-fixed: {auto_fixes_applied.length} thing{auto_fixes_applied.length > 1 ? 's' : ''}
        </div>
      )}

      {status === 'issues' && Array.isArray(findings) && findings.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
          >
            {expanded ? 'Hide findings' : `Show ${findings.length} finding${findings.length > 1 ? 's' : ''}`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1.5">
              {findings.map((f, idx) => (
                <li key={idx} className="text-xs text-gray-700 bg-gray-50 rounded p-2">
                  <span className="font-semibold">{f.check}</span>
                  {f.detail && <span className="text-gray-500"> — {f.detail}</span>}
                  {f.severity && <span className="ml-1 text-gray-400">({f.severity})</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Claude Quota Panel (CHO-365) ────────────────────────────────────────────
const Gauge = (p) => <Icon {...p} d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 118-8 8 8 0 01-8 8zm1-13h-2v6h5v-2h-3V7z" />;
const Database = (p) => <Icon {...p} d="M4 7v10c0 2 4 3 8 3s8-1 8-3V7c0-2-4-3-8-3S4 5 4 7zm8 11c-3.5 0-6-.9-6-2V9.5C8.6 10.4 10.7 11 12 11s3.4-.6 6-1.5V16c0 1.1-2.5 2-6 2z" />;

interface QuotaData {
  percent_used?: number;
  remaining?: string | number;
  reset_at?: string;
  scraped_at?: string;
  stale?: boolean;
  age_minutes?: number;
  status?: string;
  reason?: string;
}

interface UsageDay {
  period: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
}

interface UsageData {
  days?: UsageDay[];
  trailing_7d_total?: number;
  prior_7d_total?: number;
  trend_pct?: number | null;
  burn_recommendation?: string;
  generated_at?: string;
  stale?: boolean;
  age_minutes?: number;
  status?: string;
  reason?: string;
}

function useClaudeQuota() {
  const [quota, setQuota] = useState<QuotaData | null>(null);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);

  const fetchQuota = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await fetch('/api/health/claude-quota', { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.status === 'unknown' || json.status === 'error') {
        throw new Error(json.reason || 'quota source unavailable');
      }
      if (!signal?.aborted) {
        setQuota(json);
        setQuotaError(null);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!signal?.aborted) setQuotaError(err.message || 'Failed to load quota');
    } finally {
      if (!signal?.aborted) setQuotaLoading(false);
    }
  }, []);

  const fetchUsage = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await fetch('/api/health/claude-usage', { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.status === 'unknown' || json.status === 'error') {
        throw new Error(json.reason || 'usage source unavailable');
      }
      if (!signal?.aborted) {
        setUsage(json);
        setUsageError(null);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!signal?.aborted) setUsageError(err.message || 'Failed to load usage');
    } finally {
      if (!signal?.aborted) setUsageLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    setQuotaLoading(true);
    setUsageLoading(true);
    fetchQuota();
    fetchUsage();
  }, [fetchQuota, fetchUsage]);

  useEffect(() => {
    const controller = new AbortController();
    fetchQuota(controller.signal);
    fetchUsage(controller.signal);
    const id = setInterval(() => {
      fetchQuota();
      fetchUsage();
    }, 60000);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [fetchQuota, fetchUsage]);

  return {
    quota, usage,
    quotaLoading, usageLoading,
    quotaError, usageError,
    refresh,
  };
}

function ClaudeQuotaSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-2 w-full rounded-full" />
        <div className="flex gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <div className="flex gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </div>
  );
}

function recommendationClass(recommendation?: string) {
  switch (recommendation) {
    case 'under-consuming': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'over-consuming': return 'bg-red-100 text-red-700 border-red-200';
    case 'on-track': return 'bg-green-100 text-green-700 border-green-200';
    default: return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

function recommendationLabel(recommendation?: string) {
  switch (recommendation) {
    case 'under-consuming': return 'Under-consuming · room to burn';
    case 'over-consuming': return 'Over-consuming · slow down';
    case 'on-track': return 'On track';
    default: return 'Unknown';
  }
}

function ClaudeQuotaPanel() {
  const {
    quota, usage,
    quotaLoading, usageLoading,
    quotaError, usageError,
    refresh,
  } = useClaudeQuota();

  const showLoading = useDelayedLoading(quotaLoading || usageLoading);
  const quotaPct = typeof quota?.percent_used === 'number' ? quota.percent_used : 0;
  const quotaColor = quotaPct >= 90 ? 'bg-red-500' : quotaPct >= 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-indigo-500" />
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Claude Quota</h2>
        </div>
        <button
          onClick={refresh}
          className="text-gray-400 hover:text-indigo-600"
          title="Refresh quota & usage"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {showLoading ? (
        <ClaudeQuotaSkeleton />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Strategy C — authenticated quota scrape */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide">
              <Database className="w-3 h-3" />
              Strategy C · Subscription quota
            </div>
            {quotaError ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800 text-xs">
                {quotaError}
              </div>
            ) : quota ? (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{quota.percent_used}% used</span>
                    <span className="text-xs text-gray-500">Remaining: {quota.remaining ?? '—'}</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${quotaColor} transition-all`}
                      style={{ width: `${Math.min(100, Math.max(0, quotaPct))}%` }}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {quota.reset_at && (
                    <span className="text-gray-500">Resets {new Date(quota.reset_at).toLocaleDateString()}</span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${quota.stale ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                    {quota.stale ? 'Stale' : 'Fresh'} · {typeof quota.age_minutes === 'number' ? `${quota.age_minutes}m` : '—'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-gray-400 text-sm">No quota data</div>
            )}
          </div>

          {/* Strategy A — local token burn tracker */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide">
                <Database className="w-3 h-3" />
                Strategy A · 7-day burn
              </div>
              {usage?.burn_recommendation && (
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-medium ${recommendationClass(usage.burn_recommendation)}`}>
                  {recommendationLabel(usage.burn_recommendation)}
                </span>
              )}
            </div>
            {usageError ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800 text-xs">
                {usageError}
              </div>
            ) : usage && usage.days && usage.days.length > 0 ? (
              <div className="space-y-3">
                <div className="h-32 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={usage.days} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="period" tickFormatter={(p) => `${p.slice(5, 10)} ${p.slice(11, 16)}`} tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={52} tickFormatter={(v) => {
                        if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
                        if (v >= 1_000) return `${Math.round(v / 1_000)}K`;
                        return String(v);
                      }} />
                      <Tooltip />
                      <Bar dataKey="total_tokens" fill="#6366F1" name="Tokens" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span>Trailing 7d: <strong className="text-gray-700">{(usage.trailing_7d_total ?? 0).toLocaleString()}</strong></span>
                  <span>Prior 7d: <strong className="text-gray-700">{(usage.prior_7d_total ?? 0).toLocaleString()}</strong></span>
                  {typeof usage.trend_pct === 'number' && (
                    <span>Trend: <strong className={usage.trend_pct >= 0 ? 'text-red-600' : 'text-blue-600'}>{usage.trend_pct >= 0 ? '+' : ''}{usage.trend_pct}%</strong></span>
                  )}
                  <span className={`ml-auto px-2 py-0.5 rounded-full border text-[10px] font-medium ${usage.stale ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                    {usage.stale ? 'Stale' : 'Fresh'} · {typeof usage.age_minutes === 'number' ? `${usage.age_minutes}m` : '—'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-gray-400 text-sm">No local usage data yet</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Deployments Panel ───────────────────────────────────────────────────────
const Rocket = (p) => <Icon {...p} d="M12 2c0 0-7 4-7 11v3l-2 2h18l-2-2v-3c0-7-7-11-7-11zm0 14a2 2 0 110-4 2 2 0 010 4z" />;

interface Deployment { issue_id: string; repo: string; exit_code: number; timestamp: string; output: string; duration_seconds: number | null; }
interface DeploymentsResponse { deployments: Deployment[]; total: number; failure_count: number; }

function formatOutputPreview(output: string, first = 5, last = 5) {
  if (!output) return '(no output)';
  const lines = output.split('\n');
  if (lines.length <= first + last) return output;
  return [...lines.slice(0, first), `… ${lines.length - first - last} more lines …`, ...lines.slice(-last)].join('\n');
}

function DeploymentSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="border border-gray-100 rounded-lg p-3 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-gray-200" />
            <div className="h-4 bg-gray-200 rounded w-1/3" />
          </div>
          <div className="mt-2 h-3 bg-gray-200 rounded w-1/4 ml-8" />
          <div className="mt-1 h-3 bg-gray-200 rounded w-1/2 ml-8" />
        </div>
      ))}
    </div>
  );
}

function DeploymentsPanel() {
  const [data, setData] = useState<DeploymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('deployments_collapsed') || 'false'); }
    catch { return false; }
  });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c: boolean) => {
      localStorage.setItem('deployments_collapsed', JSON.stringify(!c));
      return !c;
    });
  }, []);

  const fetchDeployments = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/deployments', { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      if (!signal?.aborted) {
        setData(payload);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!signal?.aborted) setError(err.message || 'Failed to load deployment history');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchDeployments(controller.signal);
    const id = setInterval(() => fetchDeployments(), 60_000);
    return () => {
      controller.abort();
      clearInterval(id);
    };
  }, [fetchDeployments]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const failureCount = data?.failure_count ?? 0;
  const totalCount = data?.total ?? 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <button
        onClick={toggleCollapsed}
        className="w-full flex items-center justify-between mb-1"
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-2">
          <Rocket className="w-4 h-4 text-indigo-500" />
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Deployments</h2>
          {data && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full text-white ${failureCount > 0 ? 'bg-red-500' : 'bg-green-500'}`}>
              {totalCount} total{failureCount > 0 ? ` · ${failureCount} failed` : ''}
            </span>
          )}
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
      </button>

      {!collapsed && (
        <div className="mt-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-3">
              <div className="flex items-center justify-between">
                <span>Failed to load deployment history</span>
                <button
                  onClick={() => fetchDeployments()}
                  className="text-xs bg-white border border-red-200 text-red-700 px-2 py-1 rounded hover:bg-red-100"
                >
                  Retry
                </button>
              </div>
              <div className="text-xs text-red-500 mt-1">{error}</div>
            </div>
          )}

          {loading && !data && <DeploymentSkeleton />}

          {!loading && data && data.deployments.length === 0 && !error && (
            <div className="text-gray-400 text-sm py-4">No deployments yet</div>
          )}

          {data && data.deployments.length > 0 && (
            <div className="max-h-[600px] overflow-y-auto pr-1">
              <div className="space-y-2">
                {data.deployments.map((d, idx) => {
                  const rowKey = `${d.issue_id}-${d.timestamp}-${idx}`;
                  const isExpanded = expandedRows.has(rowKey);
                  const isSuccess = d.exit_code === 0;
                  return (
                    <div key={rowKey} className="border border-gray-100 rounded-lg bg-white overflow-hidden">
                      <button
                        onClick={() => toggleRow(rowKey)}
                        className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-lg leading-none mt-0.5" title={isSuccess ? 'Success' : 'Failed'}>
                            {isSuccess ? '✅' : '❌'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <a
                                href={`https://github.com/${d.repo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-sm font-medium text-gray-800 hover:text-indigo-600 truncate"
                                title={d.repo}
                              >
                                {d.repo || 'unknown repo'}
                              </a>
                              <a
                                href={`https://linear.app/issue/${d.issue_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs font-mono text-indigo-600 hover:text-indigo-700 shrink-0"
                                title="Open in Linear"
                              >
                                {d.issue_id}
                              </a>
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              <span title={new Date(d.timestamp).toLocaleString()}>{relativeTime(d.timestamp)}</span>
                              {d.duration_seconds != null && (
                                <span className="ml-2">· {Math.round(d.duration_seconds)}s</span>
                              )}
                              <span className="ml-2">· exit {d.exit_code}</span>
                            </div>
                          </div>
                          <span className="text-gray-300 text-xs shrink-0">
                            {isExpanded ? 'Hide' : 'Log'}
                          </span>
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="px-3 pb-3">
                          <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto max-h-96 overflow-y-auto font-mono leading-relaxed whitespace-pre-wrap">
                            {d.output || '(no output)'}
                          </pre>
                        </div>
                      )}
                      {!isExpanded && d.output && (
                        <div className="px-3 pb-3">
                          <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap line-clamp-3">
                            {formatOutputPreview(d.output)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ticket Completion Chart ─────────────────────────────────────────────────
const COMPLETION_COLORS = {
  Kimi: '#22c55e',   // green-500
  Hermes: '#3B82F6', // blue-500
};

function CompletionSquareMarker(props: any) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  const size = 20;
  const color = COMPLETION_COLORS[payload.implementer] || '#9CA3AF';
  return (
    <g>
      <rect x={cx - size / 2} y={cy - size / 2} width={size} height={size} rx={3} fill={color} />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={9} fontWeight={600}>
        {payload.number}
      </text>
    </g>
  );
}

function CompletionTooltip({ active, payload }: any) {
  if (active && payload && payload.length) {
    const p = payload[0]?.payload;
    if (!p) return null;
    // Scatter marker tooltip
    if (p.identifier && p.title != null) {
      return (
        <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs">
          <div className="font-semibold text-gray-700">{p.title}</div>
          <div className="text-gray-500">{p.identifier}</div>
          <div className="text-gray-500 capitalize">{p.implementer}</div>
          <div className="text-gray-400">{new Date(p.completedAt).toLocaleString()}</div>
        </div>
      );
    }
    // Area tooltip
    return (
      <div className="bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-xs">
        <div className="font-semibold text-gray-700">{p.dateLabel}</div>
        <div className="text-gray-500">Kimi: {p.Kimi}</div>
        <div className="text-gray-500">Hermes: {p.Hermes}</div>
        <div className="text-gray-400">Total: {(p.Kimi || 0) + (p.Hermes || 0)}</div>
      </div>
    );
  }
  return null;
}

function CompletionChart() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('4W');

  useEffect(() => {
    let cancelled = false;
    async function fetchCompleted() {
      try {
        const res = await fetch('/api/pipeline/completed');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setIssues(data.issues || []);
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchCompleted();
    const id = setInterval(fetchCompleted, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const rangeMs = {
    '24h': 24 * 60 * 60 * 1000,
    '3d': 3 * 24 * 60 * 60 * 1000,
    '1W': 7 * 24 * 60 * 60 * 1000,
    '2W': 14 * 24 * 60 * 60 * 1000,
    '4W': 28 * 24 * 60 * 60 * 1000,
    '2M': 60 * 24 * 60 * 60 * 1000,
    '3M': 90 * 24 * 60 * 60 * 1000,
  }[range];

  const now = Date.now();
  const filtered = issues.filter(i => i.completedAt && (now - new Date(i.completedAt).getTime()) <= rangeMs && i.implementer !== 'claude');

  const sorted = [...filtered].sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));
  const earliestMs = sorted.length > 0 ? new Date(sorted[0].completedAt).getTime() : now;
  const startMs = Math.min(earliestMs, now - rangeMs);

  const chartData = [];
  let cumKimi = 0, cumHermes = 0;
  // Zero baseline at the left edge (startMs)
  chartData.push({ date: startMs, Kimi: 0, Hermes: 0, dateLabel: new Date(startMs).toISOString().slice(0, 10) });
  for (const i of sorted) {
    if (i.implementer === 'kimi') cumKimi++;
    else cumHermes++;
    chartData.push({
      date: new Date(i.completedAt).getTime(),
      Kimi: cumKimi, Hermes: cumHermes,
      dateLabel: i.completedAt.slice(0, 10),
    });
  }
  // Extend to now with current cumulatives
  chartData.push({
    date: now,
    Kimi: cumKimi, Hermes: cumHermes,
    dateLabel: new Date(now).toISOString().slice(0, 10),
  });

  // Build scatter data for per-ticket markers
  let globalCumulative = 0;
  const scatterData = sorted.map(i => {
    globalCumulative++;
    const implementerKey = i.implementer === 'kimi' ? 'Kimi' : 'Hermes';
    const number = parseInt(i.identifier?.split('-')[1], 10) || i.identifier;
    return {
      date: new Date(i.completedAt).getTime(),
      dateLabel: i.completedAt.slice(0, 10),
      cumulative: globalCumulative,
      number,
      identifier: i.identifier,
      implementer: implementerKey,
      title: i.title,
      completedAt: i.completedAt,
    };
  });

  const showLoading = useDelayedLoading(loading);
  if (showLoading) return <CompletionChartSkeleton />;
  if (error) return <div className="text-red-500 text-xs">Completion error: {error}</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {[
            { label: '24h', value: '24h' },
            { label: '3d', value: '3d' },
            { label: '1W', value: '1W' },
            { label: '2W', value: '2W' },
            { label: '4W', value: '4W' },
            { label: '2M', value: '2M' },
            { label: '3M', value: '3M' },
          ].map(o => (
            <button
              key={o.value}
              onClick={() => setRange(o.value)}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                range === o.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="h-64">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={256}>
            <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                dataKey="date"
                domain={[startMs, now]}
                tickFormatter={(ts) => {
                  const d = new Date(ts);
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
                tick={{ fontSize: 10 }}
                minTickGap={24}
              />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip content={<CompletionTooltip />} />
              <Legend />
              <Area
                type="stepAfter"
                dataKey="Kimi"
                stackId="1"
                stroke={COMPLETION_COLORS.Kimi}
                fill={COMPLETION_COLORS.Kimi}
                fillOpacity={0.6}
              />
              <Area
                type="stepAfter"
                dataKey="Hermes"
                stackId="1"
                stroke={COMPLETION_COLORS.Hermes}
                fill={COMPLETION_COLORS.Hermes}
                fillOpacity={0.6}
              />
              <Scatter data={scatterData} dataKey="cumulative" shape={<CompletionSquareMarker />} legendType="none" />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            No completed tickets in this range
          </div>
        )}
      </div>
    </div>
  );
}

// ── Agent execution stats (CHO-127) ──────────────────────────────────────────

const OUTCOME_COLORS = {
  success: '#22c55e',
  failed: '#ef4444',
  timeout: '#f97316',
  no_changes: '#9ca3af',
  blocked: '#eab308',
};

function AgentStatsChart() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('7d');
  const [error, setError] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/agent-stats?period=${period}`)
      .then(r => r.json())
      .then(json => { if (!cancelled) { setStats(json); setError(null); } })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [period]);

  const showLoading = useDelayedLoading(!stats || !stats.agents);
  if (error) return <div className="text-red-400 text-sm">Agent stats: {error}</div>;
  if (showLoading) return <AgentStatsSkeleton />;
  if (!stats || !stats.agents) return <AgentStatsSkeleton />;

  const agentIds = Object.keys(stats.agents);
  if (agentIds.length === 0) {
    return <div className="text-gray-400 text-sm">No agent runs recorded yet.</div>;
  }

  // Merge daily data across agents for the stacked chart
  const allDates = new Set();
  for (const agent of agentIds) {
    for (const day of stats.agents[agent].daily) {
      allDates.add(day.date);
    }
  }
  const chartData = [...allDates].sort().map(date => {
    const row = { date: date.slice(5) }; // MM-DD format
    for (const agent of agentIds) {
      const day = stats.agents[agent].daily.find(d => d.date === date);
      row[`${agent}_success`] = day?.success || 0;
      row[`${agent}_failed`] = (day?.failed || 0) + (day?.timeout || 0);
      row[`${agent}_other`] = (day?.no_changes || 0) + (day?.blocked || 0);
    }
    return row;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {agentIds.map(agent => {
            const a = stats.agents[agent];
            const rate = a.success_rate != null ? `${Math.round(a.success_rate * 100)}%` : 'N/A';
            return (
              <div key={agent} className="flex items-center gap-1.5 text-sm">
                <span className="font-medium text-gray-700">{agent === 'kimi' ? 'Kimi' : 'Claude'}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-mono ${
                  a.success_rate >= 0.7 ? 'bg-green-100 text-green-700' :
                  a.success_rate >= 0.4 ? 'bg-yellow-100 text-yellow-700' :
                  a.success_rate != null ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {rate}
                </span>
                <span className="text-gray-400 text-xs">{a.total_runs} runs</span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-1">
          {['24h', '7d', '30d', '2m', '3m'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-2 py-0.5 text-xs rounded ${
                period === p ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={chartData} barGap={0} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
            <Tooltip />
            {agentIds.map(agent => (
              <Bar key={`${agent}_success`} dataKey={`${agent}_success`} stackId={agent} fill={OUTCOME_COLORS.success} name={`${agent} success`} />
            ))}
            {agentIds.map(agent => (
              <Bar key={`${agent}_failed`} dataKey={`${agent}_failed`} stackId={agent} fill={OUTCOME_COLORS.failed} name={`${agent} failed`} />
            ))}
            {agentIds.map(agent => (
              <Bar key={`${agent}_other`} dataKey={`${agent}_other`} stackId={agent} fill={OUTCOME_COLORS.no_changes} name={`${agent} other`} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}

      {/* Recent runs table */}
      <div className="mt-3">
        <h3 className="text-xs font-semibold text-gray-400 uppercase mb-1">Recent runs</h3>
        <div className="max-h-48 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-1 pr-2">Agent</th>
                <th className="text-left py-1 pr-2">Ticket</th>
                <th className="text-left py-1 pr-2">Outcome</th>
                <th className="text-left py-1 pr-2">Duration</th>
                <th className="text-left py-1">Time</th>
              </tr>
            </thead>
            <tbody>
              {agentIds.flatMap(agent =>
                stats.agents[agent].recent.map(run => (
                  <tr
                    key={run.id}
                    className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedRun(selectedRun === run.id ? null : run.id)}
                  >
                    <td className="py-1 pr-2 text-gray-600">{agent === 'kimi' ? 'Kimi' : 'Claude'}</td>
                    <td className="py-1 pr-2 font-mono text-indigo-600">{run.ticket_id || '—'}</td>
                    <td className="py-1 pr-2">
                      <span className={`px-1 py-0.5 rounded ${
                        run.outcome === 'success' ? 'bg-green-100 text-green-700' :
                        run.outcome === 'failed' ? 'bg-red-100 text-red-700' :
                        run.outcome === 'timeout' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {run.outcome || 'pending'}
                      </span>
                    </td>
                    <td className="py-1 pr-2 text-gray-400">
                      {run.duration_ms ? `${Math.round(run.duration_ms / 1000)}s` : '—'}
                    </td>
                    <td className="py-1 text-gray-400">{run.started_at?.slice(11, 16) || ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {selectedRun && (() => {
            const run = agentIds.flatMap(a => stats.agents[a].recent).find(r => r.id === selectedRun);
            return run?.error_message ? (
              <div className="mt-1 p-2 bg-red-50 text-red-700 text-xs rounded border border-red-100 font-mono whitespace-pre-wrap">
                {run.error_message}
              </div>
            ) : null;
          })()}
        </div>
      </div>
    </div>
  );
}

// ── Failing Tickets Panel (CHO-226) ───────────────────────────────────────────

interface FailingTicket {
  ticket_id: string;
  title: string;
  linear_state: string | null;
  linear_url: string;
  last_agent: string;
  last_failure_at: string;
  consecutive_failures: number;
  failure_class: string | null;
  failure_reason: string;
  debug_log_path: string | null;
  severity: 'red' | 'yellow' | 'blue';
}

function FailingTicketsPanel() {
  const [tickets, setTickets] = useState<FailingTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [blockingId, setBlockingId] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockTarget, setBlockTarget] = useState<FailingTicket | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const showLoading = useDelayedLoading(loading && tickets.length === 0);

  const fetchFailing = useCallback(async () => {
    try {
      const res = await fetch('/api/agent-stats/failing');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      setTickets(json.tickets || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFailing();

    function start() {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (document.visibilityState !== 'hidden') {
          fetchFailing();
        }
      }, 45_000);
    }

    function stop() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    start();
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') stop();
      else { start(); fetchFailing(); }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchFailing]);

  const handleRetry = async (ticket: FailingTicket) => {
    setRetryingId(ticket.ticket_id);
    try {
      const res = await fetch(`/api/agent-stats/failing/${ticket.ticket_id}/retry`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Retry failed');
      }
      // Refresh after a short delay
      setTimeout(fetchFailing, 2000);
    } catch (err: any) {
      alert(`Retry failed: ${err.message}`);
    } finally {
      setTimeout(() => setRetryingId(null), 5000);
    }
  };

  const openBlockDialog = (ticket: FailingTicket) => {
    setBlockTarget(ticket);
    setBlockReason(ticket.failure_reason);
  };

  const handleBlock = async () => {
    if (!blockTarget) return;
    setBlockingId(blockTarget.ticket_id);
    try {
      const res = await fetch(`/api/agent-stats/failing/${blockTarget.ticket_id}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: blockReason }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Block failed');
      }
      setBlockTarget(null);
      fetchFailing();
    } catch (err: any) {
      alert(`Block failed: ${err.message}`);
    } finally {
      setBlockingId(null);
    }
  };

  const handleDismiss = async (ticket: FailingTicket) => {
    setDismissingId(ticket.ticket_id);
    try {
      const res = await fetch(`/api/agent-stats/failing/${ticket.ticket_id}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours: 24 }),
      });
      if (!res.ok) throw new Error('Dismiss failed');
      setTickets(prev => prev.filter(t => t.ticket_id !== ticket.ticket_id));
    } catch (err: any) {
      alert(`Dismiss failed: ${err.message}`);
    } finally {
      setDismissingId(null);
    }
  };

  const severityDotClass = (s: FailingTicket['severity']) => {
    switch (s) {
      case 'red': return 'bg-red-500';
      case 'yellow': return 'bg-yellow-500';
      case 'blue': return 'bg-blue-500';
      default: return 'bg-gray-400';
    }
  };

  const severityLabel = (s: FailingTicket['severity']) => {
    switch (s) {
      case 'red': return 'Critical';
      case 'yellow': return 'Warning';
      case 'blue': return 'Info';
      default: return 'Unknown';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Failing tickets
          <span className="ml-2 text-xs font-normal normal-case text-gray-400">
            {tickets.length > 0 ? `(${tickets.length})` : ''}
          </span>
        </h2>
      </div>

      {error && (
        <div className="text-red-500 text-xs mb-2">Failing tickets error: {error}</div>
      )}

      {showLoading ? (
        <FailingTicketsSkeleton />
      ) : tickets.length === 0 ? (
        <div className="text-gray-500 text-sm">No failing tickets — pipeline is healthy 🟢</div>
      ) : (
        <div className="space-y-3">
          {tickets.map(ticket => (
            <div
              key={ticket.ticket_id}
              className="border border-gray-100 rounded-lg p-3 transition-opacity duration-300"
              style={{ opacity: dismissingId === ticket.ticket_id ? 0.3 : 1 }}
            >
              <div className="flex flex-wrap items-start gap-2">
                <span
                  className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${severityDotClass(ticket.severity)}`}
                  title={severityLabel(ticket.severity)}
                  aria-label={`Severity: ${severityLabel(ticket.severity)}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <a
                      href={ticket.linear_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-semibold text-indigo-600 hover:underline"
                    >
                      {ticket.ticket_id}
                    </a>
                    {ticket.failure_class && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {ticket.failure_class}
                      </span>
                    )}
                    <span className="text-gray-400 text-xs">
                      {relativeTime(ticket.last_failure_at)} · {ticket.consecutive_failures} failure{ticket.consecutive_failures !== 1 ? 's' : ''} · {ticket.last_agent}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 mt-0.5 truncate">{ticket.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{ticket.failure_reason}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-2">
                {ticket.debug_log_path ? (
                  <a
                    href={`file://${ticket.debug_log_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    View log
                  </a>
                ) : (
                  <button
                    disabled
                    title="No log available — CHO-225 not deployed yet"
                    className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-400 cursor-not-allowed"
                  >
                    View log
                  </button>
                )}
                <button
                  onClick={() => handleRetry(ticket)}
                  disabled={retryingId === ticket.ticket_id}
                  className="px-2 py-1 text-xs rounded border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-colors disabled:opacity-50"
                >
                  {retryingId === ticket.ticket_id ? 'Retrying…' : 'Retry now'}
                </button>
                <button
                  onClick={() => openBlockDialog(ticket)}
                  disabled={blockingId === ticket.ticket_id}
                  className="px-2 py-1 text-xs rounded border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
                >
                  {blockingId === ticket.ticket_id ? 'Blocking…' : 'Move to Blocked'}
                </button>
                <button
                  onClick={() => handleDismiss(ticket)}
                  disabled={dismissingId === ticket.ticket_id}
                  className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {dismissingId === ticket.ticket_id ? 'Dismissing…' : 'Dismiss'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Block confirmation dialog */}
      {blockTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Move {blockTarget.ticket_id} to Blocked?
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              This will update the Linear ticket state and leave a comment.
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
            <textarea
              value={blockReason}
              onChange={e => setBlockReason(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded p-2 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBlockTarget(null)}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBlock}
                disabled={blockingId === blockTarget.ticket_id}
                className="px-3 py-1.5 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {blockingId === blockTarget.ticket_id ? 'Moving…' : 'Move to Blocked'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Agent Configuration ───────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  spec_writer: 'Spec Writer',
  implementer: 'Implementer',
  reviewer: 'Reviewer',
};

interface AgentRoleStatus {
  primary: AgentLLMStatus;
  fallback: AgentLLMStatus;
}

interface AgentConfigPayload {
  roles: Record<string, AgentRoleStatus>;
  llms: Record<string, AgentLLMStatus>;
}

interface AgentConfigurationCardProps {
  data: AgentConfigPayload | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  activeAgents?: Record<string, { task?: string; phase?: string; title?: string } | null>;
}

function AgentConfigurationCard({ data, loading, error, onRefresh, activeAgents }: AgentConfigurationCardProps) {
  const [spin, setSpin] = useState(false);
  const showLoading = useDelayedLoading(loading);

  const handleRefresh = () => {
    setSpin(true);
    onRefresh();
    setTimeout(() => setSpin(false), 500);
  };

  if (showLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-4" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <Skeleton className="h-4 w-24" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-28" />
                <Skeleton className="h-6 w-28" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Agent Configuration</h2>
          <button
            onClick={handleRefresh}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Retry"
          >
            <RefreshCw className={`w-4 h-4 ${spin ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="text-sm text-red-600">{error}</div>
        <button
          onClick={handleRefresh}
          className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  const hasRoles = data && Object.keys(data.roles || {}).length > 0;
  if (!hasRoles) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Agent Configuration</h2>
          <button
            onClick={handleRefresh}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${spin ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="text-sm text-gray-500">
          No agent configuration found — configure in ~/.dev-pipeline/agent-config.json
        </div>
      </div>
    );
  }

  const allLlms = Object.values(data.llms || {});
  const allExhausted = allLlms.length > 0 && allLlms.every(l => l.available && l.exhausted);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Agent Configuration</h2>
        <button
          onClick={handleRefresh}
          className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${spin ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {allExhausted && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-700 text-xs mb-3">
          All LLMs currently unavailable — pipeline paused
        </div>
      )}

      <LLMStatusLegend />

      <div className="space-y-3">
        {Object.entries(data.roles).map(([roleKey, role]) => (
          <div key={roleKey} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="text-sm font-medium text-gray-700 w-32 shrink-0">
              {ROLE_LABELS[roleKey] || roleKey}
            </div>
            <div className="flex flex-wrap gap-2">
              {role.primary && (
                <LLMChip
                  status={role.primary}
                  label="Primary"
                  active={activeAgents?.[role.primary.command] != null}
                  activeDetail={
                    activeAgents?.[role.primary.command]?.phase
                      ? `${role.primary.display_name || role.primary.command} is ${activeAgents[role.primary.command]!.phase} — ${activeAgents[role.primary.command]!.task || ''}`
                      : undefined
                  }
                />
              )}
              {role.fallback && (
                <LLMChip
                  status={role.fallback}
                  label="Fallback"
                  active={activeAgents?.[role.fallback.command] != null}
                  activeDetail={
                    activeAgents?.[role.fallback.command]?.phase
                      ? `${role.fallback.display_name || role.fallback.command} is ${activeAgents[role.fallback.command]!.phase} — ${activeAgents[role.fallback.command]!.task || ''}`
                      : undefined
                  }
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Repo Mappings (CHO-377) ───────────────────────────────────────────────────

const REPO_REGEX = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;

interface RepoMapping { repo: string; localPath?: string; }

function RepoMappingsPanel() {
  const [mappings, setMappings] = useState<Record<string, RepoMapping>>({});
  const [source, setSource] = useState<'config' | 'default' | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const showLoading = useDelayedLoading(loading);

  const fetchMappings = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/pipeline/repo-mappings', { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!signal?.aborted) {
        setMappings(json.mappings || {});
        setSource(json.source || 'default');
        setUpdatedAt(json.updatedAt || null);
      }
    } catch (err: any) {
      if (err.name !== 'AbortError' && !signal?.aborted) {
        setError(err.message || 'Failed to load repo mappings');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchMappings(controller.signal);
    return () => controller.abort();
  }, [fetchMappings]);

  const validate = (): string | null => {
    for (const [project, entry] of Object.entries(mappings)) {
      if (!project.trim()) return 'Project name cannot be empty';
      if (!entry.repo.trim()) return `Repo is required for "${project}"`;
      if (!REPO_REGEX.test(entry.repo)) return `Repo "${entry.repo}" must match owner/repo format`;
      if (entry.localPath !== undefined && entry.localPath.trim() === '') {
        return `localPath for "${project}" cannot be empty`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/pipeline/repo-mappings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`);
      }
      setMappings(json.mappings);
      setUpdatedAt(json.updatedAt);
      setSource('config');
      setSuccess('Repo mappings saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateProject = (oldProject: string, newProject: string) => {
    setMappings(prev => {
      const next: Record<string, RepoMapping> = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k === oldProject ? newProject : k] = v;
      }
      return next;
    });
  };

  const updateRepo = (project: string, repo: string) => {
    setMappings(prev => ({ ...prev, [project]: { ...prev[project], repo } }));
  };

  const updateLocalPath = (project: string, localPath: string) => {
    setMappings(prev => {
      const next = { ...prev };
      if (localPath.trim() === '') {
        const { localPath: _, ...rest } = next[project];
        next[project] = rest;
      } else {
        next[project] = { ...next[project], localPath };
      }
      return next;
    });
  };

  const addMapping = () => {
    setMappings(prev => {
      let i = 1;
      while (prev[`New Project ${i}`]) i++;
      return { ...prev, [`New Project ${i}`]: { repo: '' } };
    });
  };

  const removeMapping = (project: string) => {
    setMappings(prev => {
      const next = { ...prev };
      delete next[project];
      return next;
    });
  };

  if (showLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <Skeleton className="h-4 w-40 mb-3" />
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-2">
              <Skeleton className="h-8 flex-1" />
              <Skeleton className="h-8 flex-1" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GitHub className="w-4 h-4 text-indigo-500" />
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Repo Mappings</h2>
          {source && (
            <span className={`text-xs px-2 py-0.5 rounded-full border ${
              source === 'config'
                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                : 'bg-gray-100 text-gray-600 border-gray-200'
            }`}>
              {source === 'config' ? 'Custom' : 'Defaults'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {updatedAt && (
            <span className="text-xs text-gray-400" title={updatedAt}>
              saved {relativeTime(updatedAt)}
            </span>
          )}
          <button
            onClick={() => fetchMappings()}
            className="text-gray-400 hover:text-indigo-600"
            title="Refresh mappings"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs mb-3">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-xs mb-3">
          {success}
        </div>
      )}

      <div className="space-y-2 mb-3">
        {Object.entries(mappings).map(([project, entry]) => {
          const repoInvalid = entry.repo.trim() !== '' && !REPO_REGEX.test(entry.repo);
          return (
            <div key={project} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
              <input
                type="text"
                value={project}
                onChange={e => updateProject(project, e.target.value)}
                placeholder="Linear project"
                className="sm:col-span-3 text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="text"
                value={entry.repo}
                onChange={e => updateRepo(project, e.target.value)}
                placeholder="owner/repo"
                className={`sm:col-span-4 text-sm border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  repoInvalid ? 'border-red-300 bg-red-50' : 'border-gray-200'
                }`}
              />
              <input
                type="text"
                value={entry.localPath || ''}
                onChange={e => updateLocalPath(project, e.target.value)}
                placeholder="local path (optional)"
                className="sm:col-span-3 text-sm border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => removeMapping(project)}
                className="sm:col-span-2 px-2 py-1.5 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50"
              >
                Remove
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={addMapping}
          className="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          + Add mapping
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-xs px-4 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PipelinePage() {
  const [data, setData] = useState(null);
  const { relay, queue, linear, errors } = data || {};
  const [error, setError] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [ageSec, setAgeSec] = useState(0);
  const [agentConfig, setAgentConfig] = useState<AgentConfigPayload | null>(null);
  const [agentConfigLoading, setAgentConfigLoading] = useState(true);
  const [agentConfigError, setAgentConfigError] = useState<string | null>(null);
  const summaryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const auxIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(1000);
  const wsClosingByUsRef = useRef(false);

  // Project filter state (client-side multi-select)
  const PROJECT_FILTER_KEY = 'pipeline_project_filter_v2';
  const [projectFilter, setProjectFilter] = useState<{ hidden: string[]; hideNoProject: boolean }>(() => {
    // Remove the legacy single-select key; it stored an unusable value.
    try {
      localStorage.removeItem('pipeline_project_filter');
    } catch { /* ignore */ }
    try {
      const raw = localStorage.getItem(PROJECT_FILTER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          Array.isArray(parsed.hidden) &&
          typeof parsed.hideNoProject === 'boolean'
        ) {
          return {
            hidden: parsed.hidden.filter((h: unknown) => typeof h === 'string'),
            hideNoProject: parsed.hideNoProject,
          };
        }
      }
    } catch {
      // Malformed stored value degrades to all-visible and is overwritten below.
    }
    return { hidden: [], hideNoProject: false };
  });

  // Animation state tracking
  const [cardStates, setCardStates] = useState({}); // { [issueId]: 'entering' | null }
  const [leavingCards, setLeavingCards] = useState({}); // { [phantomKey]: { issue, oldState, timestamp } }
  const prevLinearRef = useRef<any>(null);
  const leavingTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({}); // { [phantomKey]: timeoutId }

  // DnD optimistic move state
  const [optimisticByState, setOptimisticByState] = useState<Record<string, any[]> | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const pendingMoveRef = useRef<{ ticketId: string; targetState: string } | null>(null);

  // History expansion state
  const [expandedCardId, setExpandedCardId] = useState(null);
  const historyCache = useRef<Record<string, any[]>>({}); // { [issueId]: historyArray }

  // Fetch agent configuration
  const fetchAgentConfig = useCallback(async () => {
    try {
      const json = await api.getAgents();
      setAgentConfig(json);
      setAgentConfigError(null);
    } catch (err: any) {
      setAgentConfigError(err.message || 'Failed to load agent config');
    } finally {
      setAgentConfigLoading(false);
    }
  }, []);

  // Fetch tick history
  const fetchTicks = useCallback(async () => {
    try {
      const res = await fetch('/api/pipeline/ticks');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      setTicks(json.ticks || []);
      setTicksError(null);
    } catch (err: any) {
      setTicksError(err.message);
    }
  }, []);

  // Fetch smoke summary (one request per board refresh)
  const fetchSmokeSummary = useCallback(async (signal?: AbortSignal) => {
    try {
      const res = await fetch('/api/pipeline/smoke', { signal });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      if (!signal?.aborted) {
        setSmokeSummary(json.byIdentifier || {});
        setSmokeSummaryError(null);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!signal?.aborted) setSmokeSummaryError(err.message || 'Failed to load smoke summary');
    }
  }, []);

  // Health state
  const [healthData, setHealthData] = useState(null);
  const [healthError, setHealthError] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [healthRange, setHealthRange] = useState('24h');
  const [selectedBucket, setSelectedBucket] = useState(null);

  // Pipeline cron health state
  const [pipelineCron, setPipelineCron] = useState(null);
  const [pipelineCronError, setPipelineCronError] = useState(null);
  const [activeAgents, setActiveAgents] = useState(null);

  // Tick history state
  const [ticks, setTicks] = useState<any[] | null>(null);
  const [selectedTick, setSelectedTick] = useState<any>(null);
  const [ticksError, setTicksError] = useState<string | null>(null);

  // Smoke verdict state
  const [smokeSummary, setSmokeSummary] = useState<Record<string, SmokeSummary> | null>(null);
  const [smokeSummaryError, setSmokeSummaryError] = useState<string | null>(null);
  const smokeDetailCache = useRef<Record<string, SmokeDetail>>({});

  // Apply a summary payload (from WS push or fallback poll) and animate card movements.
  const applySummary = useCallback((json: any) => {
    const prevLinear = prevLinearRef.current;
    if (prevLinear && json.linear) {
      const prevById: Record<string, any> = {};

      // Build map of previous issues by ID
      LINEAR_STATES.forEach(state => {
        const issues = prevLinear.byState?.[state] || [];
        issues.forEach((issue: any) => {
          prevById[issue.identifier] = { ...issue, state };
        });
      });

      // Build map of current issues by ID
      const currById: Record<string, any> = {};
      LINEAR_STATES.forEach(state => {
        const issues = json.linear.byState?.[state] || [];
        issues.forEach((issue: any) => {
          currById[issue.identifier] = { ...issue, state };
        });
      });

      // Find moved cards and create leaving phantoms
      Object.keys(currById).forEach(id => {
        const prevIssue = prevById[id];
        const currIssue = currById[id];

        if (prevIssue && prevIssue.state !== currIssue.state) {
          // Card moved! Mark new as entering, create phantom in old column
          const timestamp = Date.now();
          const phantomKey = `${id}-leaving-${timestamp}`;

          // Mark entering in the new column
          setCardStates(prev => ({ ...prev, [id]: 'entering' }));

          // Create phantom in the old column
          setLeavingCards(prev => ({
            ...prev,
            [phantomKey]: { issue: prevIssue, oldState: prevIssue.state, timestamp }
          }));

          // Clear entering state after animation
          setTimeout(() => {
            setCardStates(prev => {
              const next = { ...prev };
              if (next[id] === 'entering') delete next[id];
              return next;
            });
          }, 2400);

          // Clean up phantom after animation
          const timeoutId = setTimeout(() => {
            setLeavingCards(prev => {
              const next = { ...prev };
              delete next[phantomKey];
              return next;
            });
            delete leavingTimeoutsRef.current[phantomKey];
          }, 2400);

          leavingTimeoutsRef.current[phantomKey] = timeoutId;
        }
      });
    }

    prevLinearRef.current = json.linear;
    setData(json);
    setActiveAgents(json.activeAgents || null);
    setError(null);
    setLastRefreshedAt(Date.now());
    setAgeSec(0);
    // Only clear optimistic drag state once the backend summary reflects the move.
    setOptimisticByState(prev => {
      if (!prev) return null;
      const pending = pendingMoveRef.current;
      if (pending) {
        const reachedTarget = json.linear?.byState?.[pending.targetState]?.some(
          (issue: any) => issue.identifier === pending.ticketId
        );
        if (!reachedTarget) {
          // Backend cache is still behind the optimistic move; keep it.
          return prev;
        }
        pendingMoveRef.current = null;
      }
      return null;
    });
  }, []);

  // Stable fetchData — cardStates read via functional setCardStates, not in deps
  const fetchData = useCallback(async () => {
    // Board refresh: invalidate stale per-issue history cache.
    historyCache.current = {};
    try {
      const res = await fetch('/api/pipeline/summary');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      applySummary(json);
    } catch (err: any) {
      setError(err.message);
    }
  }, [applySummary]); // applySummary is stable

  // 60-second fallback poll for the summary (safety net), plus 5-second aux polls
  // and a 30-second smoke-summary poll.
  const smokeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchData();
    fetchTicks();
    fetchSmokeSummary();

    function startSummaryPoll() {
      if (summaryIntervalRef.current) return;
      summaryIntervalRef.current = setInterval(() => {
        if (document.visibilityState !== 'hidden') fetchData();
      }, 60_000);
    }

    function startAuxPoll() {
      if (auxIntervalRef.current) return;
      auxIntervalRef.current = setInterval(() => {
        if (document.visibilityState !== 'hidden') fetchTicks();
      }, 5000);
    }

    function startSmokePoll() {
      if (smokeIntervalRef.current) return;
      smokeIntervalRef.current = setInterval(() => {
        if (document.visibilityState !== 'hidden') fetchSmokeSummary();
      }, 30_000);
    }

    function stopSummaryPoll() {
      if (summaryIntervalRef.current) {
        clearInterval(summaryIntervalRef.current);
        summaryIntervalRef.current = null;
      }
    }

    function stopAuxPoll() {
      if (auxIntervalRef.current) {
        clearInterval(auxIntervalRef.current);
        auxIntervalRef.current = null;
      }
    }

    function stopSmokePoll() {
      if (smokeIntervalRef.current) {
        clearInterval(smokeIntervalRef.current);
        smokeIntervalRef.current = null;
      }
    }

    startSummaryPoll();
    startAuxPoll();
    startSmokePoll();
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopSummaryPoll();
        stopAuxPoll();
        stopSmokePoll();
      } else {
        startSummaryPoll();
        startAuxPoll();
        startSmokePoll();
        fetchData();
        fetchSmokeSummary();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopSummaryPoll();
      stopAuxPoll();
      stopSmokePoll();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchData, fetchTicks, fetchSmokeSummary]);

  // WebSocket push channel: connects on mount, reconnects with backoff, pauses when hidden.
  const disconnectWs = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const ws = wsRef.current;
    if (ws) {
      // Detach handlers and mark as intentionally closed so onclose does not reconnect.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      wsClosingByUsRef.current = true;
      ws.close();
      wsRef.current = null;
    }
  }, []);

  const connectWs = useCallback(() => {
    disconnectWs();
    wsClosingByUsRef.current = false;
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${protocol}://${location.host}/api/pipeline/ws`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectDelayRef.current = 1000;
    };

    ws.onmessage = (event) => {
      try {
        const json = JSON.parse(event.data);
        applySummary(json);
      } catch (err) {
        console.error('[pipeline-ws] message parse error:', err);
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (wsClosingByUsRef.current) {
        // Intentional disconnect (unmount/tab hidden): do not reconnect.
        return;
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connectWs();
      }, reconnectDelayRef.current);
      reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30_000);
    };

    ws.onerror = () => {
      // Let onclose schedule the reconnect.
    };
  }, [applySummary, disconnectWs]);

  useEffect(() => {
    connectWs();
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') disconnectWs();
      else connectWs();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      disconnectWs();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [connectWs, disconnectWs]);

  // Fetch agent config on mount and every 60s
  useEffect(() => {
    fetchAgentConfig();
    const id = setInterval(fetchAgentConfig, 60_000);
    return () => clearInterval(id);
  }, [fetchAgentConfig]);

  // Persist project filter
  useEffect(() => {
    try {
      localStorage.setItem(PROJECT_FILTER_KEY, JSON.stringify(projectFilter));
    } catch { /* ignore */ }
  }, [projectFilter]);

  // Age counter (increments every second)
  useEffect(() => {
    const tick = setInterval(() => {
      if (lastRefreshedAt) setAgeSec(Math.round((Date.now() - lastRefreshedAt) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastRefreshedAt]);

  // Fetch health current status
  useEffect(() => {
    async function fetchHealth() {
      try {
        const res = await fetch('/api/health');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setHealthData(json);
        setHealthError(null);
      } catch (err: any) {
        setHealthError(err.message);
      }
    }
    fetchHealth();
    const iv = setInterval(fetchHealth, 30000);
    return () => clearInterval(iv);
  }, []);

  // Fetch pipeline cron health
  useEffect(() => {
    async function fetchPipelineCron() {
      try {
        const res = await fetch('/api/health/pipeline-cron');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setPipelineCron(json);
        setPipelineCronError(null);
      } catch (err: any) {
        setPipelineCronError(err.message);
      }
    }
    fetchPipelineCron();
    const iv = setInterval(fetchPipelineCron, 60000);
    return () => clearInterval(iv);
  }, []);

  // Fetch health history when range changes, then poll every 30s
  useEffect(() => {
    let cancelled = false;
    async function fetchHistory() {
      try {
        const res = await fetch(`/api/health/history?range=${healthRange}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setHistoryData(json);
      } catch (err: any) {
        if (!cancelled) console.error('Failed to fetch health history:', err);
      }
    }
    fetchHistory();
    const id = setInterval(fetchHistory, 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchHistory();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [healthRange]);

  const isStale = ageSec > 30;
  const summaryLoading = useDelayedLoading(!data);

  const handleToggleCard = useCallback((cardId: string) => {
    setExpandedCardId(prev => prev === cardId ? null : cardId);
  }, []);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const ticketId = active.id as string;
    const targetState = over.id as string;

    const currentByState = linear?.byState;
    if (!currentByState) return;

    let sourceState: string | null = null;
    let issue: any = null;
    for (const state of LINEAR_STATES) {
      const found = currentByState[state]?.find((i: any) => i.identifier === ticketId);
      if (found) {
        sourceState = state;
        issue = found;
        break;
      }
    }

    if (!sourceState || sourceState === targetState) return;

    const nextByState: Record<string, any[]> = {};
    for (const state of LINEAR_STATES) {
      if (state === sourceState) {
        nextByState[state] = currentByState[state].filter((i: any) => i.identifier !== ticketId);
      } else if (state === targetState) {
        nextByState[state] = [...currentByState[state], issue];
      } else {
        nextByState[state] = [...currentByState[state]];
      }
    }
    setOptimisticByState(nextByState);
    pendingMoveRef.current = { ticketId, targetState };

    try {
      const res = await fetch('/api/pipeline/move-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: ticketId, target_state: targetState }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Move failed');
      }
    } catch (err: any) {
      setMoveError(err.message || 'Move failed');
      setOptimisticByState(null);
      pendingMoveRef.current = null;
      setTimeout(() => setMoveError(null), 4000);
    }
  }, [linear]);

  const displayLinear = useMemo(() => {
    if (!linear || !optimisticByState) return linear;
    const totals: Record<string, number> = {};
    for (const state of LINEAR_STATES) {
      totals[state] = (optimisticByState[state] || []).length;
    }
    return { ...linear, byState: optimisticByState, totals };
  }, [linear, optimisticByState]);

  // Derive the project list from the loaded issues (no API call).
  const projectOptions = useMemo(() => {
    const projects = new Set<string>();
    let hasNoProject = false;
    if (linear?.byState) {
      for (const state of LINEAR_STATES) {
        for (const issue of linear.byState[state] || []) {
          if (issue.project) projects.add(issue.project);
          else hasNoProject = true;
        }
      }
    }
    return { projects: [...projects].sort(), hasNoProject };
  }, [linear]);

  const hiddenProjects = useMemo(
    () => new Set(projectFilter.hidden),
    [projectFilter.hidden]
  );

  const isIssueVisible = useCallback((issue: any) => {
    if (!issue.project) return !projectFilter.hideNoProject;
    return !hiddenProjects.has(issue.project);
  }, [hiddenProjects, projectFilter.hideNoProject]);

  const filteredLinear = useMemo(() => {
    if (!displayLinear) return displayLinear;
    const byState: Record<string, any[]> = {};
    for (const state of LINEAR_STATES) {
      byState[state] = (displayLinear.byState?.[state] || []).filter(isIssueVisible);
    }
    const totals: Record<string, number> = {};
    for (const state of LINEAR_STATES) totals[state] = byState[state].length;
    return { ...displayLinear, byState, totals };
  }, [displayLinear, isIssueVisible]);

  const filteredLeavingCards = useMemo(() => {
    if (!leavingCards) return leavingCards;
    const next: Record<string, { issue: any; oldState: string; timestamp: number }> = {};
    for (const [key, phantom] of Object.entries(leavingCards)) {
      if (isIssueVisible(phantom.issue)) next[key] = phantom;
    }
    return next;
  }, [leavingCards, isIssueVisible]);

  const unfilteredTotal = useMemo(() => (
    linear ? Object.values(linear.totals || {}).reduce((a, b) => a + b, 0) : 0
  ), [linear]);

  const filteredTotal = useMemo(() => (
    filteredLinear ? Object.values(filteredLinear.totals || {}).reduce((a, b) => a + b, 0) : 0
  ), [filteredLinear]);

  const filterEmpty = unfilteredTotal > 0 && filteredTotal === 0;

  const handleResetProjectFilter = useCallback(() => {
    setProjectFilter({ hidden: [], hideNoProject: false });
  }, []);

  return (
    <div className="space-y-6">
      <AnimationStyles />
      
      {/* ── Header bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {data && <StatusPill relay={relay} queue={queue} />}
          
          {/* Project Filter Dropdown */}
          <ProjectFilterDropdown options={projectOptions} value={projectFilter} onChange={setProjectFilter} />
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span className={isStale ? 'text-orange-500 font-medium' : ''}>
            {lastRefreshedAt ? `Last refreshed: ${ageSec}s ago${isStale ? ' (stale)' : ''}` : <Spinner size={16} />}
          </span>
          <button
            onClick={fetchData}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Refresh now"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {moveError && (
            <span className="text-xs text-red-600 max-w-xs truncate" title={moveError}>{moveError}</span>
          )}
        </div>
      </div>

      {/* ── Global fetch error ── */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          Error: {error}
        </div>
      )}

      {/* ── Collector errors (non-fatal) ── */}
      {errors?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-700 text-xs">
          <strong>Some data collectors failed:</strong>{' '}
          {errors.map(e => `${e.source}: ${e.message}`).join(' · ')}
        </div>
      )}

      {/* ── Daemon panel ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Daemon</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          {/* Relay */}
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Relay daemon</div>
            {summaryLoading ? (
              <Skeleton className="h-4 w-32" />
            ) : relay ? (
              <>
                <div className={`font-semibold ${relay.running ? 'text-green-600' : 'text-red-600'}`}>
                  {relay.running ? `Running (PID ${relay.pid})` : 'Not running'}
                </div>
                {!relay.running && relay.lastExitCode != null && (
                  <div className="text-red-400 text-xs">Last exit: {relay.lastExitCode}</div>
                )}
              </>
            ) : <div className="text-gray-400">—</div>}
          </div>

          {/* Queue */}
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Queue</div>
            {summaryLoading ? (
              <div className="space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-16" />
              </div>
            ) : queue ? (
              <>
                <div className="text-gray-700">
                  <span className="font-semibold">{queue.pending}</span> pending
                </div>
                <div className={`text-xs ${queue.failedTotal > 0 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  {queue.failedTotal} failed
                </div>
              </>
            ) : <div className="text-gray-400">—</div>}
          </div>
        </div>
      </div>

      {/* ── Agent Configuration ── */}
      <AgentConfigurationCard
        data={agentConfig}
        loading={agentConfigLoading}
        error={agentConfigError}
        onRefresh={fetchAgentConfig}
        activeAgents={data?.activeAgents as Record<string, { task?: string; phase?: string; title?: string } | null> | undefined}
      />

      {/* ── Repo Mappings (CHO-377) ── */}
      <RepoMappingsPanel />

      {/* ── Health panel ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Service Health</h2>
          {healthData && (
            <span className={`text-xs font-semibold px-2 py-1 rounded-full text-white ${healthData.overall === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`}>
              {healthData.overall === 'healthy' ? 'All healthy' : 'Degraded'}
            </span>
          )}
          {healthData && healthData.mutations_enabled === false && (
            <span className="text-xs font-semibold px-2 py-1 rounded-full text-white bg-red-600">
              Mutating API disabled
            </span>
          )}
        </div>

        {healthData && healthData.mutations_enabled === false && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-3">
            <strong>Mutating API disabled.</strong> API_SHARED_SECRET is not set — all write actions (kanban moves, settings, invoice email, etc.) will fail with 503. See ops/README.md.
          </div>
        )}

        {healthError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs mb-3">
            Health check failed: {healthError}
          </div>
        )}

        {healthData && healthData.overall !== 'healthy' && !healthError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-3">
            <strong>Alert:</strong>{' '}
            {Object.entries(healthData.services || {})
              .filter(([, s]) => s.status !== 'ok' && s.status !== 'idle')
              .map(([id, s]) => `${s.display_name || id} is ${s.status}`)
              .join(' · ')}
          </div>
        )}

        {pipelineCronError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-xs mb-3">
            Pipeline cron check failed: {pipelineCronError}
          </div>
        )}

        <div className="mb-4">
          <PipelineHealthTile data={pipelineCron} />
        </div>

        <UptimeChart
          healthData={healthData}
          historyData={historyData}
          range={healthRange}
          setRange={setHealthRange}
          selectedBucket={selectedBucket}
          setSelectedBucket={setSelectedBucket}
        />
      </div>

      {/* ── Claude Quota (CHO-365) ── */}
      <ClaudeQuotaPanel />

      {/* ── Deployments ── */}
      <DeploymentsPanel />

      {/* ── Tick prediction ── */}
      {data?.tickPrediction && (
        <TickPredictionCard prediction={data.tickPrediction} />
      )}

      {/* ── Tick history slider ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Tick history</h2>
        {ticksError && (
          <div className="text-red-500 text-xs mb-2">{ticksError}</div>
        )}
        <TickSlider
          ticks={ticks}
          selectedTick={selectedTick}
          onSelectTick={setSelectedTick}
        />
      </div>

      {/* ── Linear board ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Linear board
            {filteredLinear && (
              <span className="ml-2 text-indigo-500 font-normal normal-case">
                ({Object.values(filteredLinear.totals || {}).reduce((a, b) => a + b, 0)} active)
              </span>
            )}
            <NextTickPill agentTick={data?.agentTick} />
          </h2>
          <OwnerLegend />
        </div>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        >
          {smokeSummaryError && (
            <div className="text-xs text-red-600 mb-2">
              Smoke summary error: {smokeSummaryError}
            </div>
          )}
          <LinearBoard
            linear={filteredLinear}
            leavingCards={filteredLeavingCards}
            cardStates={cardStates}
            expandedCardId={expandedCardId}
            onToggleCard={handleToggleCard}
            historyCache={historyCache}
            activeAgents={activeAgents}
            loading={summaryLoading}
            isDragging={!!activeDragId}
            filterEmpty={filterEmpty}
            onResetFilter={handleResetProjectFilter}
            smokeSummary={smokeSummary}
            smokeDetailCache={smokeDetailCache}
          />
          <DragOverlay>
            {activeDragId ? (
              <DragOverlayCard issue={findIssueInLinear(linear, activeDragId)} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* ── Failing tickets (CHO-226) ── */}
      <FailingTicketsPanel />

      {/* ── Ticket completions ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Ticket completions</h2>
        <CompletionChart />
      </div>

      {/* ── Block timeline (CHO-307) ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <BlockTimelineChart />
      </div>

      {/* ── Agent execution stats (CHO-127) ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Agent execution stats</h2>
        <AgentStatsChart />
      </div>

      {/* ── Recent activity ── */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Recent activity</h2>
        <RecentActivity queue={queue} relay={relay} loading={summaryLoading} />
      </div>

      {/* ── Failed panel ── */}
      <div>
        <FailedPanel queue={queue} />
      </div>


    </div>
  );
}
